import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { resolveCapturedAudioSessionSyncModelUuid } from "@/modules/uuid/sync-models/resolveCapturedAudioSessionSyncModelUuid";
import { VoiceAnalyticsTracker } from "@/store/audio-capture/analytics/voice-analytics-tracker";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import { ProcessingManager } from "@/store/audio-capture/managers/processing-manager";
import {
  loadDexieRecordingPayload,
  resolveRecordingState,
} from "@/store/audio-capture/processing/dexie-recording-loader";
import type { RecordingUploadPayload } from "@/store/audio-capture/managers/processing-manager";

export class ProcessRecordingWorkflow {
  constructor(
    private readonly store: AppStoreAudioCaptureStore,
    private readonly analytics: VoiceAnalyticsTracker,
    private readonly processing: ProcessingManager
  ) {}

  async run() {
    const processRecordingStartTime = Date.now();

    const recordingId = this.store.currentSession?.recordingId;
    const noteIdHint = this.store.currentSession?.noteId ?? this.store.activeNoteId;
    const recordingState = await resolveRecordingState({
      repository: this.store.store.memDb.audioRecordings,
      recordingId,
      noteId: noteIdHint,
    });

    if (!recordingState) {
      toastModule.triggerToast({
        content: "Recording data is unavailable",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[ProcessRecordingWorkflow] Missing recording state for upload",
        info: { recordingId: recordingId ?? null, noteId: noteIdHint ?? null },
      });
      return;
    }

    if (
      ![
        RecordingStatus.PAUSED,
        RecordingStatus.STOPPING,
        RecordingStatus.STOPPED,
        RecordingStatus.UPLOADED,
        RecordingStatus.LOCKED,
      ].includes(this.store.status)
    ) {
      toastModule.triggerToast({
        content: "Cannot process recording when the recorder is not stopped",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] processRecording",
        info: { error: "Cannot process recording when the recorder is not stopped" },
      });
      return;
    }

    this.store.activeNoteObservable?.setIsProcessingSuggestion(true);

    const shouldUpload = [
      RecordingStatus.STOPPING,
      RecordingStatus.STOPPED,
      RecordingStatus.LOCKED,
    ].includes(this.store.status);

    const payloadBase = await loadDexieRecordingPayload({
      repository: this.store.store.memDb.audioRecordings,
      recordingId: recordingState.recordingId ?? recordingId,
      noteId: recordingState.noteId ?? noteIdHint,
      requireAudio: shouldUpload,
    });

    if (!payloadBase) {
      toastModule.triggerToast({
        content: "Could not prepare recording for upload",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[ProcessRecordingWorkflow] Failed to build Dexie payload",
        info: {
          recordingId: recordingState.recordingId ?? recordingId ?? null,
          noteId: noteIdHint ?? null,
        },
      });
      this.store.activeNoteObservable?.setIsProcessingSuggestion(false);
      return;
    }

    const noteId = payloadBase.noteId;
    const capturedAudioId = payloadBase.capturedAudioId;
    const sessionId = resolveCapturedAudioSessionSyncModelUuid({ capturedAudioId });

    if (shouldUpload) {
      this.store.setStatus(RecordingStatus.UPLOADING);
      const uploadPayload = buildUploadPayload(payloadBase);
      if (!uploadPayload) {
        toastModule.triggerToast({
          content: "Recording audio is missing",
          toastId: this.store.capturedAudioId,
        });
        logger.error({
          message: "[ProcessRecordingWorkflow] Missing audio blob for upload",
          info: { recordingId: payloadBase.recordingId },
        });
        this.store.setStatus(RecordingStatus.STOPPED);
        this.store.error = "Recording audio is missing";
        this.store.activeNoteObservable?.setIsProcessingSuggestion(false);
        return;
      }

      const result = await this.processing.uploadRecording(uploadPayload);
      if (!result.success) {
        const uploadError = result.error;
        toastModule.triggerToast({
          content: uploadError || "Failed to upload recording",
          toastId: this.store.capturedAudioId,
        });
        logger.error({
          message: "[AppStoreAudioCaptureStore] processRecording, upload failed",
          info: { error: uploadError || "Failed to upload recording" },
        });
        this.store.setStatus(RecordingStatus.STOPPED);
        this.store.error = uploadError || "Failed to upload recording";
        this.store.activeNoteObservable?.setIsProcessingSuggestion(false);
        return;
      } else {
        this.store.setStatus(RecordingStatus.UPLOADED);
        this.store.error = null;
      }
    }

    const uploadFinishedTime = Date.now();
    const uploadDuration = Math.floor((uploadFinishedTime - processRecordingStartTime) / 1000);

    this.store.setStatus(RecordingStatus.PROCESSING);
    const { success: transformSuccess, error: transformError } =
      await this.processing.transformAudioToNote({
        noteId,
        capturedAudioId,
        sessionId,
      });
    if (!transformSuccess) {
      toastModule.triggerToast({
        content: transformError || "Failed to transform audio to note",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] processRecording, transform failed",
        info: { error: transformError || "Failed to transform audio to note" },
      });
      this.store.setStatus(RecordingStatus.UPLOADED);
      this.store.error = transformError || "Failed to transform audio to note";
      this.store.activeNoteObservable?.setIsProcessingSuggestion(false);
      return;
    }

    const processRecordingEndTime = Date.now();
    const transformNoteDuration = Math.floor((processRecordingEndTime - uploadFinishedTime) / 1000);
    const totalDuration = Math.floor((processRecordingEndTime - processRecordingStartTime) / 1000);

    logger.info({
      message: "[AppStoreAudioCaptureStore] processRecording completed",
      info: {
        capturedAudioId,
        uploadDurationSeconds: uploadDuration,
        transformDurationSeconds: transformNoteDuration,
        totalDurationSeconds: totalDuration,
      },
    });

    this.analytics.suggestedEditsViewed({
      sessionId,
      capturedAudioId,
      noteId,
      uploadDurationSeconds: uploadDuration,
      transformDurationSeconds: transformNoteDuration,
      totalDurationSeconds: totalDuration,
    });

    this.store.activeNoteObservable?.setIsProcessingSuggestion(false);

    this.store.clearRecording();
  }
}

function buildUploadPayload(payload: {
  audioBlob?: Blob;
  capturedAudioId: string;
  transcriptText: string;
  transcriptArray: RecordingUploadPayload["transcriptArray"];
  preferredMimeType?: string;
}): RecordingUploadPayload | null {
  if (!payload.audioBlob) return null;

  return {
    audioBlob: payload.audioBlob,
    capturedAudioId: payload.capturedAudioId,
    transcriptText: payload.transcriptText,
    transcriptArray: payload.transcriptArray,
    preferredMimeType: payload.preferredMimeType,
  };
}
